import { Typography } from '@material-ui/core';
import { useCallback, useEffect, useRef } from 'react';
import type {
  ManagementTimelineMarker,
  ManagementTimelineQuery,
} from '@internal/spassu-code-contract';
import type {
  CockpitArtifactContentState,
  CockpitTimelineState,
} from '../controller/cockpitControllerTypes';
import type {
  CockpitNodeKind,
  CockpitViewModel,
  CockpitViewNode,
} from '../model/cockpitViewModel';
import { allCockpitNodes } from '../model/cockpitViewModel';
import type { CockpitSelectionState } from '../selection/cockpitSelection';
import { ArtifactsSection } from './ArtifactsSection';
import { CoverageSection } from './CoverageSection';
import { EvidenceFlow } from './EvidenceFlow';
import { MetricsSection } from './MetricsSection';
import { TimelineSection } from './TimelineSection';
import { useCockpitStyles } from './cockpitStyles';

type SelectionController = CockpitSelectionState & {
  select(kind: CockpitNodeKind, id: string): void;
  clear(): void;
};

export interface CockpitShellProps {
  model: CockpitViewModel;
  stale?: boolean;
  showArtifactLane?: boolean;
  metricsPreferenceScope?: string | null;
  selection: SelectionController;
  artifactContent?: Record<string, CockpitArtifactContentState>;
  loadArtifactContent?: (artifactId: string) => Promise<string>;
  loadMoreArtifactContent?: (artifactId: string) => Promise<string>;
  timeline: CockpitTimelineState;
  setTimelineQuery(query: ManagementTimelineQuery): void;
  loadMoreTimeline(): void;
}

/**
 * The selection reveals itself inside the node that represents it, so the shell
 * only announces it and explains the case where the node is not in this
 * revision — there is no panel left to say it.
 */
const SelectionAnnouncement = ({
  className,
  model,
  selection,
  timelineMarkers,
}: {
  className: string;
  model: CockpitViewModel;
  selection: CockpitSelectionState['selected'];
  timelineMarkers?: ManagementTimelineMarker[];
}) => (
  <>
    {selection && (
      <Typography variant="caption">
        {selectionCaption(model, selection, timelineMarkers)}
      </Typography>
    )}
    <div className={className} aria-live="polite">
      {selection
        ? `Selecionado: ${selection.id}. ${selectionCaption(
            model,
            selection,
            timelineMarkers,
          )}`
        : 'Seleção limpa'}
    </div>
  </>
);

function selectionCaption(
  model: CockpitViewModel,
  selection: NonNullable<CockpitSelectionState['selected']>,
  timelineMarkers?: ManagementTimelineMarker[],
): string {
  const revision = `Seleção da revisão ${selection.revision ?? 'viva'}.`;
  return isSelectionResolvable(model, selection, timelineMarkers)
    ? revision
    : `${revision} O nó relacionado não está disponível nesta revisão.`;
}

/** Timeline markers are not evidence-flow nodes, so they never highlight one. */
function evidenceNodeKind(
  selected: CockpitSelectionState['selected'],
): CockpitViewNode['kind'] | null {
  if (!selected || selected.kind === 'timeline') return null;
  return selected.kind;
}

function isSelectionResolvable(
  model: CockpitViewModel,
  selection: NonNullable<CockpitSelectionState['selected']>,
  timelineMarkers?: ManagementTimelineMarker[],
): boolean {
  if (selection.kind === 'timeline') {
    return Boolean(timelineMarkers?.some(marker => marker.id === selection.id));
  }
  return allCockpitNodes(model).some(
    node => node.id === selection.id && node.kind === selection.kind,
  );
}

type CockpitSectionsProps = Pick<
  CockpitShellProps,
  | 'model'
  | 'selection'
  | 'artifactContent'
  | 'loadArtifactContent'
  | 'loadMoreArtifactContent'
  | 'stale'
  | 'metricsPreferenceScope'
  | 'showArtifactLane'
> & {
  timeline: CockpitTimelineState;
  setTimelineQuery: CockpitShellProps['setTimelineQuery'];
  loadMoreTimeline: CockpitShellProps['loadMoreTimeline'];
  select: SelectionController['select'];
};

const CockpitEvidenceSections = ({
  model,
  selection,
  select,
  stale,
  metricsPreferenceScope,
  showArtifactLane,
}: Pick<
  CockpitSectionsProps,
  | 'model'
  | 'selection'
  | 'select'
  | 'stale'
  | 'metricsPreferenceScope'
  | 'showArtifactLane'
>) => (
  <>
    <CoverageSection
      rail={model.rail}
      selectedId={
        evidenceNodeKind(selection.selected) ? selection.selected!.id : null
      }
      onSelect={id => select('skill', id)}
    />
    <MetricsSection
      metrics={model.metrics}
      stale={stale}
      preferenceScope={metricsPreferenceScope}
    />
    <EvidenceFlow
      showArtifactLane={showArtifactLane}
      skills={model.skills}
      artifacts={model.artifacts}
      relations={model.relations}
      sectionState={model.sectionState.relations}
      selectedId={
        evidenceNodeKind(selection.selected) ? selection.selected!.id : null
      }
      selectedKind={evidenceNodeKind(selection.selected)}
      relatedIds={selection.relatedIds}
      onSelect={select}
    />
  </>
);

const CockpitSections = (props: CockpitSectionsProps) => {
  const { model, selection, select } = props;
  const classes = useCockpitStyles();
  const selected = selection.selected;
  const selectedIdOf = (kind: CockpitNodeKind) =>
    selected?.kind === kind ? selected.id : null;
  return (
    <div className={classes.shell}>
      <CockpitEvidenceSections
        model={model}
        selection={selection}
        select={select}
        stale={props.stale}
        metricsPreferenceScope={props.metricsPreferenceScope}
        showArtifactLane={props.showArtifactLane}
      />
      <ArtifactsSection
        artifacts={model.artifacts}
        sectionState={model.sectionState.artifacts}
        selectedId={selectedIdOf('artifact')}
        onSelect={id => select('artifact', id)}
        artifactContent={props.artifactContent}
        loadArtifactContent={props.loadArtifactContent}
        loadMoreArtifactContent={props.loadMoreArtifactContent}
      />
      <TimelineSection
        timeline={props.timeline}
        model={model}
        selectedId={selectedIdOf('timeline')}
        onSelect={id => select('timeline', id)}
        onQueryChange={props.setTimelineQuery}
        onLoadMore={props.loadMoreTimeline}
      />
    </div>
  );
};

export const CockpitShell = (props: CockpitShellProps) => {
  const { model, selection } = props;
  const classes = useCockpitStyles();
  const focusSelection = useFocusRestoringSelection(selection);
  // no selection has a panel of its own any more, so the shell owns Escape
  useEscapeToClear(Boolean(selection.selected), focusSelection.clear);
  return (
    <main className={classes.shellLayout}>
      <CockpitSections {...props} select={focusSelection.select} />
      <SelectionAnnouncement
        className={classes.live}
        model={model}
        selection={selection.selected}
        timelineMarkers={props.timeline.page?.markers}
      />
    </main>
  );
};

function useFocusRestoringSelection(selection: SelectionController) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const select = useCallback(
    (kind: CockpitNodeKind, id: string) => {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      selection.select(kind, id);
    },
    [selection],
  );
  const clear = useCallback(() => {
    selection.clear();
    const trigger = triggerRef.current;
    window.setTimeout(() => trigger?.focus(), 0);
  }, [selection]);
  return { select, clear };
}

function useEscapeToClear(active: boolean, clear: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clear();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [active, clear]);
}
